import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  delay,
  distinctUntilChanged,
  finalize,
  map,
  pairwise,
} from 'rxjs/operators';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { RequestCardComponent } from './request-card/request-card.component';
import { UrlSearchComponent } from '../../../../../ui-components/lib/search-field/url-search/url-search.component';
import { PaginatorComponent } from '../../../../../ui-components/lib/paginator/paginator.component';
import { TabComponent } from '../../../../../ui-components/lib/tabs/tab/tab.component';
import { TabsComponent } from '../../../../../ui-components/lib/tabs/tabs.component';
import { TableEmptyStateComponent } from '../../../../../ui-components/lib/table/table-empty-state/table-empty-state.component';
import { OverlayLoaderComponent } from '../../../../common/components/overlay-loader/overlay-loader.component';
import { RequestService } from '../../../../common/services/request/request.service';
import { Tab } from '../../../../common/models/tab.model';
import {
  ApiResponse,
  Meta,
} from '../../../../common/models/api-response.model';
import { SearchEnum } from '../../../../common/enums/search.enum';
import { WorkflowCategory } from '../../../../common/models/workflow-category.model';
import { RequestCard } from '../../../../common/models/request.model';
import { EMPLOYEE_ROUTES } from '../../../../common/constants/employee.routing.constants';
import { of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  injectSystemTabsLayout,
  injectFloatingLayout,
  provideSystemTabsLayout,
  injectFloatingPageClasses,
} from '../../../../common/helpers/system-tabs-layout/system-tabs-layout';
import { FilteredTableComponent } from '../../../../common/components/filtered-table/filtered-table.component';
import { FilteredTablePayload } from '../../../../common/components/filtered-table/filtered-table.model';
import { DEFAULT_PAGE_SIZE } from '../../../../common/constants/pagination.constants';
import { CookieService } from '../../../../common/services/cookie/cookie.service';
import { RequestActionsComponent } from './request-actions/request-actions.component';
import {
  REQUEST_LIST_CATEGORY_FILTER_KEY,
  REQUEST_LIST_COLUMNS,
} from './request-list.constants';
import { ChipFilter } from '../../../../common/components/chips-filter/chips-filter.model';
import { INSIGHT_CHART_FALLBACK_COLOR } from '../../../../common/components/insight-charts/insight-chart.model';

@Component({
  selector: 'lib-request-list',
  imports: [
    NgTemplateOutlet,
    TabsComponent,
    TabComponent,
    PaginatorComponent,
    RequestCardComponent,
    TranslocoPipe,
    OverlayLoaderComponent,
    TableEmptyStateComponent,
    UrlSearchComponent,
    FilteredTableComponent,
    RequestActionsComponent,
  ],
  templateUrl: './request-list.component.html',
  styleUrl: './request-list.component.scss',
  providers: [provideSystemTabsLayout()],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RequestListComponent implements OnInit {
  protected readonly systemTabs = injectSystemTabsLayout();
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();
  transloco = inject(TranslocoService);
  route = inject(ActivatedRoute);
  destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  readonly REQUEST_LIST_TAB_PARAM_KEY = EMPLOYEE_ROUTES.REQUESTS.ACTION.VIEW;
  readonly currentKeyword = signal<string>('');
  readonly urlSearch = viewChild(UrlSearchComponent);
  noDataImgDimensions = signal<{ width: string; height: string }>({
    width: '84px',
    height: '80px',
  });
  private readonly requestService = inject(RequestService);

  requestCategoryList = signal<Tab[]>([
    {
      id: 0,
      name: this.transloco.translate('requests_screen_first_tab_label'),
      total: '',
    },
  ]);
  selectedCategory = signal<Tab>(this.requestCategoryList()[0]);
  categoriesLoading = signal<boolean>(true);
  requestList = signal<RequestCard[]>([]);
  page = signal<number>(1);
  pageSize = signal<number>(24);
  totalPages = signal<number>(1);
  totalItems = signal<number>(0);
  isLoading = signal<boolean>(false);

  // UI_Version_2: grid/list views, search and pagination come from lib-filtered-table
  private readonly cookieService = inject(CookieService);
  private static readonly VIEW_PREFERENCE_KEY = 'request_list_gallery_view';
  readonly filteredTable = viewChild(FilteredTableComponent);
  /** Rendered once the categories are known, so the chips and the first load use them. */
  readonly tableReady = signal(false);
  /** Categories as a single-choice chips filter; the table keeps the pick in the URL as category_id. */
  readonly categoryFilters = signal<ChipFilter[]>([]);
  /** Grid by default; the last grid/list choice is remembered, like the products list. */
  readonly isGalleryView = signal(
    this.cookieService.get(RequestListComponent.VIEW_PREFERENCE_KEY) !== 'false'
  );
  readonly meta = signal<Meta | undefined>(undefined);
  readonly totalWorkflows = signal(0);
  readonly payload = signal<FilteredTablePayload>({
    page: 1,
    size: DEFAULT_PAGE_SIZE,
    search: '',
    filters: {},
    sortColumn: { key: '', value: 'asc' },
    tab: { key: '', label: '', active: true },
  });
  readonly columns = computed(() =>
    REQUEST_LIST_COLUMNS.map(column => ({
      ...column,
      header: column.header ? this.transloco.translate(column.header) : '',
    }))
  );
  readonly fallbackCategoryColor = INSIGHT_CHART_FALLBACK_COLOR;

  readonly loadRows$ = () => {
    const payload = this.payload();
    this.currentKeyword.set(payload.search);
    const categoryId = payload.filters[REQUEST_LIST_CATEGORY_FILTER_KEY];
    this.selectCategoryFromChip(
      Array.isArray(categoryId) ? categoryId[0] : categoryId
    );
    this.isLoading.set(true);
    return this.requestService
      .getRequestList(
        payload.page,
        payload.size,
        this.selectedCategory().id,
        payload.search
      )
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      );
  };

  setGalleryView(isGallery: boolean) {
    this.isGalleryView.set(isGallery);
    this.cookieService.set(
      RequestListComponent.VIEW_PREFERENCE_KEY,
      String(isGallery)
    );
  }

  loadRows() {
    this.loadRows$().subscribe({
      next: res => this.applyRows(res),
      error: () => this.meta.set(undefined),
    });
  }

  applyRows(res: any) {
    this.handleRequestListSuccessResponse(res);
    this.meta.set(res.meta);
  }

  ngOnInit() {
    this.getWorkflowCategories();
    // The filtered table reloads on search itself under UI_Version_2
    if (!this.floating()) {
      this.handleParamsChange();
    }
  }

  handleParamsChange() {
    this.route.queryParams
      .pipe(
        map(params => params[SearchEnum.keyword] ?? ''),
        distinctUntilChanged(),
        pairwise(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([prev, curr]) => {
        if (curr || prev) {
          if (!curr) this.page.set(1);
          this.getRequestList(curr);
        }
      });
  }

  getRequestList(search = '') {
    this.isLoading.set(true);
    this.requestService
      .getRequestList(
        this.page(),
        this.pageSize(),
        this.selectedCategory().id,
        search
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => this.handleRequestListSuccessResponse(res),
        error: () => this.isLoading.set(false),
      });
  }

  getWorkflowCategories() {
    this.requestService
      .getWorkflowCategories()
      .subscribe((res: ApiResponse<WorkflowCategory>) => {
        this.requestCategoryList().length = 1;
        const workflowCategories = res.data;
        workflowCategories.forEach((category: WorkflowCategory) => {
          this.requestCategoryList().push({
            id: category.value,
            name: category.label,
            total: category.flow_templates_count.toString(),
          });
        });
        this.totalWorkflows.set(
          workflowCategories.reduce(
            (sum, category) => sum + category.flow_templates_count,
            0
          )
        );
        this.categoriesLoading.set(false);
        if (this.floating()) {
          this.categoryFilters.set([
            {
              key: REQUEST_LIST_CATEGORY_FILTER_KEY,
              label: this.transloco.translate('requests_category_filter_label'),
              allCount: this.totalWorkflows(),
              options: this.requestCategoryList()
                .slice(1)
                .map(category => ({
                  value: category.id.toString(),
                  label: category.name,
                  count: Number(category.total),
                })),
            },
          ]);
          this.tableReady.set(true);
          return;
        }
        of(true)
          .pipe(delay(100))
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.restoreTabFromUrl();
          });
      });
  }

  private restoreTabFromUrl() {
    const viewParam =
      this.route.snapshot.queryParams[this.REQUEST_LIST_TAB_PARAM_KEY];
    if (viewParam) {
      const tabIndex = this.requestCategoryList().findIndex(
        category => category.id.toString() === viewParam
      );
      if (tabIndex > -1) {
        this.selectedCategory.set(this.requestCategoryList()[tabIndex]);
      }
    }
    const keyword = this.route.snapshot.queryParams[SearchEnum.keyword] ?? '';
    this.getRequestList(keyword);
  }

  handleRequestListSuccessResponse(res: any) {
    this.isLoading.set(false);
    this.requestList.set(
      res.data.map((r: any) => ({
        ...r,
        ...this.requestService.mapSummaryDetailsResponse(r).data,
      }))
    );

    this.totalPages.set(res.meta.last_page);
    this.totalItems.set(res.meta.total);
    // set total items for all category
    if (this.selectedCategory().id === 0 && !this.currentKeyword()) {
      this.requestCategoryList()[0].total = this.totalItems().toString();
    }

    // set total items for selected category
    this.selectedCategory.update(prev => ({
      ...prev,
      total: res.meta.total.toString(),
    }));
  }

  handlePagination($event: number) {
    this.page.set($event);
    this.getRequestList(this.currentKeyword());
  }

  handleTabClick(tabIndex: number) {
    this.page.set(1);
    this.currentKeyword.set('');
    this.selectedCategory.set(this.requestCategoryList()[tabIndex]);
    const hasSearch = !!this.route.snapshot.queryParams[SearchEnum.keyword];
    if (hasSearch) {
      this.urlSearch()?.clear();
    } else {
      this.getRequestList('');
    }
  }

  /** The chips filter value is the category id; none is All. */
  private selectCategoryFromChip(key: string | undefined) {
    const category = this.requestCategoryList().find(
      item => item.id.toString() === (key || '0')
    );
    if (category && category.id !== this.selectedCategory().id) {
      this.selectedCategory.set(category);
    }
  }
}
